"use client";

import { useState, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import { createClient } from "@/lib/supabase/client";

interface TargetData {
  brand: string;
  reason: string;
  since: string;
  tweet_text: string | null;
  competitor: string | null;
}

const FALLBACK: TargetData = {
  brand: "StreamFlix Premium",
  reason: "Running 4+ ad breaks per hour on paid tier subscribers",
  since: "Feb 10, 2026",
  tweet_text: "Hey @StreamFlixPremium — I PAY for your service. That means NO ADS. I'm switching to your competitor until you respect paid subscribers. #PaidMeansPaid 🚫📺",
  competitor: "ClearStream",
};

export default function TargetBanner() {
  const supabase = createClient();
  const [tweeted, setTweeted] = useState(false);
  const [target, setTarget] = useState<TargetData>(FALLBACK);

  useEffect(() => {
    supabase
      .from("target_of_week")
      .select("brand, reason, since, tweet_text, competitor")
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setTarget(data);
      });
  }, []);

  const handleTweet = () => {
    const text = target.tweet_text ?? `Hey @${target.brand.replace(/\s+/g, "")} — I PAY for your service. That means NO ADS. #PaidMeansPaid`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setTweeted(true);
  };

  return (
    <section
      id="target-banner"
      className="relative bg-[#0D0D0D] text-white overflow-hidden"
      aria-label="Current boycott target"
    >
      {/* Top red stripe */}
      <div className="h-1 w-full bg-[#FF2222]" />
      <div className="max-w-8xl mx-auto px-6 md:px-12 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">

          {/* Left: Target info */}
          <div className="flex items-start gap-5">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF2222] flex items-center justify-center blink-alert">
              <Icon name="ExclamationTriangleIcon" size={22} variant="solid" className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-xs font-bold uppercase tracking-widest text-[#FF2222]">
                  🚨 Current Target
                </span>
                <span className="text-xs text-white/40 font-medium">Since {target.since}</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-black text-white leading-tight tracking-tight">
                {target.brand}
              </h2>
              <p className="text-sm text-white/60 mt-1 max-w-xl font-light">
                {target.reason}
              </p>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex flex-wrap gap-3 items-center">
            <button
              onClick={handleTweet}
              className={`group flex items-center gap-2.5 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 ${
                tweeted ? "bg-green-500 text-white" : "bg-[#FF2222] text-white hover:bg-[#CC0000] btn-red-pulse"
              }`}
              aria-label="Tweet at current target brand"
            >
              {tweeted ? (
                <>
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="animate-check-pop" />
                  Tweeted! Keep pushing
                </>
              ) : (
                <>
                  <Icon name="ChatBubbleLeftRightIcon" size={16} variant="outline" />
                  Tweet at Them Now
                  <Icon name="ArrowTopRightOnSquareIcon" size={14} variant="outline" className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </>
              )}
            </button>

            {target.competitor && (
              <div className="text-xs text-white/40 font-medium flex items-center gap-1.5">
                <Icon name="ArrowsRightLeftIcon" size={13} variant="outline" />
                Switch to{" "}
                <span className="text-white/80 font-semibold">{target.competitor}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="h-px w-full bg-white/5" />
    </section>
  );
}